import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { FaCopy, FaSync } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import config from '../config';

const server_URL = config.SERVER_URL;

const ApiLogs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [events, setEvents] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);

    if (userData.role_id === 1) {
      fetchCustomers();
      fetchEvents(); // role 1: start with common events
    } else if (userData.role_id === 2) {
      fetchEvents(userData.id); // role 2: their events + role1
    }

    fetchLogs(userData.role_id, userData.id, customerFilter, eventFilter);
  }, []);

  useEffect(() => {
    // Update events when customer filter changes (role 1)
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.role_id === 1) {
      fetchEvents(customerFilter || null);
    }
  }, [customerFilter]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchEvents = async (customerId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'get_events',
        user_role: userData.role_id,
        user_id: userData.id,
      };

      if (customerId) {
        requestBody.customer_id = customerId;
      }

      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEvents(data.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const fetchLogs = async (role = userRole, userId = loggedUserId, custFilter = customerFilter, evFilter = eventFilter) => {
    setIsLoading(true);
    try {
      const response = await fetch(server_URL + '/api/api_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_role: role,
          user_id: userId,
          customer_id: role === 1 ? custFilter : undefined,
          event_key: evFilter || undefined,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setLogs(data.data);
      } else {
        showToast(data.error || 'Failed to fetch API logs', 'error');
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching API logs:', error);
      showToast('Error fetching API logs', 'error');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = () => {
    fetchLogs();
  };

  const handleClear = () => {
    setCustomerFilter('');
    setEventFilter('');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.role_id === 1) {
      fetchEvents(null);
      fetchLogs(userData.role_id, userData.id, '', '');
    } else {
      fetchEvents(userData.id);
      fetchLogs(userData.role_id, userData.id, '', '');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const formatCustomFields = (cf) => {
    if (!cf) return '—';
    if (typeof cf === 'string') return cf;
    try {
      return JSON.stringify(cf, null, 2);
    } catch (e) {
      return String(cf);
    }
  };

  // Unauthorized
  if (userRole !== 1 && userRole !== 2) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert severity="error">
          <Typography variant="h6">Unauthorized Access</Typography>
          <Typography variant="body1">
            You do not have permission to access this page.
          </Typography>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            API Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View latest 500 API event submissions.
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {userRole === 1 && (
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel>Filter by Customer</InputLabel>
              <Select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                label="Filter by Customer"
                size="small"
              >
                <MenuItem value="">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Event</InputLabel>
            <Select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              label="Filter by Event"
              size="small"
            >
              <MenuItem value="">All Events</MenuItem>
              {events.map((event) => (
                <MenuItem key={event.id} value={event.key}>
                  {event.name} ({event.key})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" onClick={handleFilter}>
              Filter
            </Button>
            <Button variant="outlined" color="warning" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => fetchLogs()}>
              Refresh
            </Button>
          </Stack>
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {userRole === 1 && <TableCell><strong>Customer</strong></TableCell>}
                  <TableCell><strong>Event</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>First Name</strong></TableCell>
                  <TableCell><strong>Last Name</strong></TableCell>
                  <TableCell><strong>Custom Fields</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userRole === 1 ? 7 : 6} align="center" sx={{ py: 5 }}>
                      <Typography variant="h6" color="text.secondary">
                        No logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((row) => (
                    <TableRow key={row.id} hover>
                      {userRole === 1 && (
                        <TableCell>
                          <Typography variant="body2">{row.customer_username || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customer_email || 'N/A'}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={row.event_key} size="small" sx={{ fontFamily: 'monospace' }} />
                          {row.event_name && (
                            <Typography variant="caption" color="text.secondary">
                              {row.event_name}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.first_name}</TableCell>
                      <TableCell>{row.last_name}</TableCell>
                      <TableCell>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {formatCustomFields(row.custom_fields)}
                        </pre>
                      </TableCell>
                      <TableCell>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ApiLogs;

